import { FC, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '@store/auth/AuthProvider'
import { useDemoMode } from '@utils/demoMode'
import type { UserPermissions } from '@zonasocioscofarte/types'
import {
  LayoutDashboard,
  ShoppingCart,
  FileText,
  Truck,
  ArrowLeftRight,
  GitCompareArrows,
  UserCircle,
  Users,
  Pill,
  Bell,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Search,
  Shield,
  Download,
  Building2,
  BarChart3,
  PackageOpen,
  CalendarDays,
  Wallet,
  Receipt,
  Clock,
  RotateCcw,
  TrendingUp,
  Store,
  Package,
  FilePlus,
  Monitor,
  Printer,
  AlertTriangle,
  ExternalLink,
  ClipboardCheck,
  ListTodo,
  FolderOpen,
  CalendarClock,
  LifeBuoy,
  Phone
} from 'lucide-react'
import { cn } from '@utils/index'
import { openPdfViewer } from '@utils/pdfViewer'

interface MenuItem {
  path: string
  label: string
  icon: React.ElementType
  permission: string | null
  external?: boolean
  children?: MenuItem[]
  modalPdf?: { title: string; src: string }
}

const menuItems: MenuItem[] = [
  { path: '/', label: 'Inicio', icon: LayoutDashboard, permission: null },
  { path: '/pendientes', label: 'Centro de control', icon: ListTodo, permission: null },
  { path: '/buscar-cubeta', label: 'Buscar Cubeta', icon: Search, permission: 'aalbaranes' },
  { path: '/documentos', label: 'Documentos', icon: FolderOpen, permission: 'afacturas' },
  { path: '/soporte', label: 'Soporte Web', icon: LifeBuoy, permission: null },
  {
    path: '/attendance',
    label: 'Asistencia Remota',
    icon: Shield,
    permission: 'apedidos',
    children: [
      { path: '/attendance/security-code', label: 'Código seguridad', icon: Shield, permission: 'apedidos' },
      { path: '/attendance/downloads', label: 'Descargar', icon: Download, permission: 'apedidos' },
    ]
  },
  {
    path: '/mifarmacia',
    label: 'MiFarmacia',
    icon: Building2,
    permission: null,
    children: [
      { path: '/orders', label: 'Pedidos', icon: ShoppingCart, permission: 'apedidos' },
      {
        path: '/transfers',
        label: 'Transfer',
        icon: ArrowLeftRight,
        permission: 'atransferfacil',
        children: [
          { path: '/transfers/gestion', label: 'Gestión Transfer', icon: BarChart3, permission: 'atransferfacil' },
          { path: '/transfers/facil', label: 'Transfer Fácil', icon: ArrowLeftRight, permission: 'atransferfacil' },
          { path: '/transfers/facil-agrupado', label: 'Transfer Fácil (Agrupado)', icon: ArrowLeftRight, permission: 'atransferfacil' },
          { path: '/transfers/grupo', label: 'Transfer Grupo', icon: ArrowLeftRight, permission: 'atransfergestion' },
          { path: '/transfers/grupo-agrupado', label: 'Transfer Grupo (Agrupado)', icon: ArrowLeftRight, permission: 'atransfergestion' },
        ]
      },
      { path: '/delivery-notes', label: 'Albaranes', icon: Truck, permission: 'aalbaranes' },
      { path: '/albaranes-vencimiento', label: 'Albaranes Vencimiento', icon: CalendarClock, permission: 'aalbaranes' },
      { path: '/delivery-notes/contrast', label: 'Contrastar albaranes', icon: GitCompareArrows, permission: 'aalbaranes' },
      { path: '/recepcion', label: 'Recepción', icon: ClipboardCheck, permission: 'aalbaranes' },
      {
        path: '/estupefacientes',
        label: 'Estupefacientes',
        icon: Pill,
        permission: 'aestupe',
        children: [
          { path: '/estupefacientes/pedidos', label: 'Pedidos Estupefacientes', icon: Pill, permission: 'aestupe' },
          { path: '/estupefacientes/informe-resumen', label: 'Informe Resumen', icon: BarChart3, permission: 'afacturas' },
          { path: '/estupefacientes/informe-detallado', label: 'Informe Detallado', icon: BarChart3, permission: 'afacturas' },
        ]
      },
      { path: '/aviso-disponibilidad', label: 'Aviso Disponibilidad', icon: Bell, permission: 'adisponibilidad' },
      { path: '/sms', label: 'Envío SMS', icon: MessageSquare, permission: 'asms' },
      { path: '/partial-deliveries', label: 'Entregas Parciales', icon: PackageOpen, permission: 'aentregas' },
      {
        path: '/facturacion',
        label: 'Facturación',
        icon: FileText,
        permission: 'afacturas',
        children: [
          { path: '/invoices', label: 'Facturas', icon: FileText, permission: 'afacturas' },
          { path: '/invoices/calendar', label: 'Calendario vencimientos', icon: CalendarDays, permission: 'afacturas' },
          { path: '/invoices/pending-due', label: 'Pte.Vencimiento', icon: Clock, permission: 'afacturas' },
          { path: '/aportaciones', label: 'Aportaciones', icon: Wallet, permission: 'afacturas' },
          { path: '/condiciones', label: 'Condiciones grupo facturación', icon: Receipt, permission: 'afacturas' },
        ]
      },
      {
        path: '/logistica-inversa',
        label: 'Logística inversa',
        icon: RotateCcw,
        permission: 'adevolucion',
        children: [
          { path: '/devoluciones', label: 'Caducidad y retirada', icon: RotateCcw, permission: 'adevolucion' },
          { path: '/devoluciones-ds', label: 'Devoluciones y DS', icon: RotateCcw, permission: 'adevolucion' },
          { path: '/cedifa', label: 'CEDIFA', icon: RotateCcw, permission: null },
        ]
      },
      {
        path: '/informes-compras',
        label: 'Informes Compras',
        icon: BarChart3,
        permission: 'afacturas',
        children: [
          { path: '/informes-compras/tramo', label: 'Tramo', icon: TrendingUp, permission: 'afacturas' },
          { path: '/informes-compras/nivel-acofarma', label: 'Nivel ACOFARMA', icon: BarChart3, permission: 'afacturas' },
          { path: '/informes-compras/histo-compras', label: 'Histo compras', icon: BarChart3, permission: 'afacturas' },
          { path: '/informes-compras/categorias', label: 'Categorías', icon: BarChart3, permission: 'afacturas' },
          { path: '/informes-compras/vet-antibioticos', label: 'Vet. antibióticos', icon: BarChart3, permission: 'afacturas' },
        ]
      },
      {
        path: '/analisis-datos',
        label: 'Análisis de datos',
        icon: TrendingUp,
        permission: 'abi',
        children: [
          { path: '/analisis-datos/logistica-inversa', label: 'Logística inversa', icon: RotateCcw, permission: 'abi' },
          { path: '/analisis-datos/compras', label: 'Compras', icon: BarChart3, permission: 'abi' },
          { path: '/analisis-datos/estupefacientes', label: 'Estupefacientes', icon: Pill, permission: 'abi' },
        ]
      },
    ]
  },
  {
    path: '/micooperativa',
    label: 'MiCooperativa',
    icon: Store,
    permission: null,
    children: [
      { path: '/encargos', label: 'Encargos', icon: Package, permission: 'aencargos' },
      { path: '#seguimiento-envios', label: 'Seguimiento envíos', icon: Truck, permission: null, modalPdf: { title: 'Seguimiento envíos', src: '/SeguimientoEnvios.pdf' } },
      { path: '/altas', label: 'Nuevas altas', icon: FilePlus, permission: 'aconsulta' },
      { path: '/delegados', label: 'Laboratorios y delegados', icon: Phone, permission: null },
    ]
  },
  {
    path: '/dpto-informatica',
    label: 'Dpto. Informática',
    icon: Monitor,
    permission: null,
    children: [
      { path: 'https://www.unycop.es', label: 'UNYCOP', icon: ExternalLink, permission: 'amiunycop', external: true },
      { path: '/farmaturn', label: 'FarmaTurn', icon: Monitor, permission: 'amiunycop' },
      { path: '/consumibles', label: 'Pedidos Consumibles', icon: Printer, permission: null },
      { path: '/incidencias', label: 'Notificar Incidencia', icon: AlertTriangle, permission: null },
    ]
  },
  { path: '/users', label: 'Gestión de Usuarios', icon: Users, permission: 'ausuarios' },
  { path: '/profile', label: 'Mi Perfil', icon: UserCircle, permission: null },
]

interface SidebarProps {
  onNavigate?: () => void
  collapsed?: boolean
}

export const Sidebar: FC<SidebarProps> = ({ onNavigate, collapsed = false }) => {
  const { permissions, user } = useAuth()
  const demoMode = useDemoMode()
  const location = useLocation()
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    '/transfers': true
  })
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null)

  const hasPermission = (permission: string | null): boolean => {
    if (!permission) return true
    if (!permissions) return false
    return permissions[permission as keyof UserPermissions] ?? false
  }

  const hasVisibleChildren = (item: MenuItem): boolean => {
    if (!item.children) return false
    return item.children.some(child => hasPermission(child.permission))
  }

  const toggleMenu = (path: string) => {
    setExpandedMenus(prev => ({ ...prev, [path]: !prev[path] }))
  }

  const isMenuActive = (item: MenuItem): boolean => {
    if (location.pathname === item.path) return true
    if (item.children) {
      return item.children.some(child => isMenuActive(child))
    }
    return false
  }

  const renderCollapsedItem = (item: MenuItem) => {
    const Icon = item.icon
    const isActive = isMenuActive(item)
    const visibleChildren = hasVisibleChildren(item)

    if (!hasPermission(item.permission)) return null
    if (item.children && !visibleChildren) return null

    return (
      <div 
        key={item.path + item.label}
        className="relative group"
        onMouseEnter={() => visibleChildren && setHoveredMenu(item.path)}
        onMouseLeave={() => setHoveredMenu(null)}
      >
        {visibleChildren ? (
          <>
            <button
              className={cn(
                "w-full flex items-center justify-center p-3 rounded-lg transition-all duration-200",
                isActive
                  ? "bg-cofarte-purple text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple"
              )}
              title={item.label}
            >
              <Icon className={cn("h-5 w-5", isActive ? "text-white" : "text-gray-500")} />
            </button>
            {/* Floating submenu */}
            {hoveredMenu === item.path && (
              <div className="absolute left-full top-0 ml-2 bg-white border border-gray-200 rounded-xl shadow-lg p-2 min-w-[200px] z-50">
                <div className="text-xs font-semibold text-gray-500 px-3 py-2 border-b border-gray-100 mb-1">
                  {item.label}
                </div>
                {item.children?.map(child => {
                  if (!hasPermission(child.permission)) return null
                  return (
                    <div key={child.path + child.label}>
                      {child.external ? (
                        <a
                          href={child.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple transition-colors"
                        >
                          <child.icon className="h-4 w-4" />
                          {child.label}
                        </a>
                      ) : child.modalPdf ? (
                        <button
                          onClick={() => { openPdfViewer(child.modalPdf!); onNavigate?.() }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple transition-colors"
                        >
                          <child.icon className="h-4 w-4" />
                          {child.label}
                        </button>
                      ) : (
                        <NavLink
                          to={child.path}
                          end
                          onClick={onNavigate}
                          className={({ isActive }) =>
                            cn(
                              "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                              isActive
                                ? "bg-cofarte-purple text-white"
                                : "text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple"
                            )
                          }
                        >
                          <child.icon className="h-4 w-4" />
                          {child.label}
                        </NavLink>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        ) : item.external ? (
          <a
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            className={cn(
              "flex items-center justify-center p-3 rounded-lg transition-all duration-200",
              "text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple"
            )}
            title={item.label}
          >
            <Icon className="h-5 w-5 text-gray-500" />
          </a>
        ) : item.modalPdf ? (
          <button
            onClick={() => { openPdfViewer(item.modalPdf!); onNavigate?.() }}
            className={cn(
              "w-full flex items-center justify-center p-3 rounded-lg transition-all duration-200",
              "text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple"
            )}
            title={item.label}
          >
            <Icon className="h-5 w-5 text-gray-500" />
          </button>
        ) : (
          <NavLink
            to={item.path}
            end
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center justify-center p-3 rounded-lg transition-all duration-200",
                isActive
                  ? "bg-cofarte-purple text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple"
              )
            }
            title={item.label}
          >
            <Icon className={cn("h-5 w-5", location.pathname === item.path ? "text-white" : "text-gray-500")} />
          </NavLink>
        )}
      </div>
    )
  }

  const renderExpandedItem = (item: MenuItem, depth = 0) => {
    const Icon = item.icon
    const visibleChildren = hasVisibleChildren(item)
    const isExpanded = expandedMenus[item.path]
    const isActive = isMenuActive(item)

    if (!hasPermission(item.permission)) return null
    if (item.children && !visibleChildren) return null

    return (
      <div key={item.path + item.label}>
        {visibleChildren ? (
          <>
            <button
              onClick={() => toggleMenu(item.path)}
              className={cn(
                "w-full flex items-center justify-between gap-3 px-3 rounded-lg font-medium transition-all duration-200",
                depth === 0 ? "py-2.5 text-sm" : "py-2 text-xs",
                isActive
                  ? "bg-cofarte-purple text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple"
              )}
            >
              <div className="flex items-center gap-3">
                <Icon className={cn(
                  depth === 0 ? "h-5 w-5" : "h-4 w-4",
                  isActive ? "text-white" : "text-gray-500"
                )} />
                {item.label}
              </div>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            {isExpanded && item.children && (
              <div className={cn("mt-1 space-y-1", depth === 0 ? "ml-3" : "ml-3")}>
                {item.children.map(child => renderExpandedItem(child, depth + 1))}
              </div>
            )}
          </>
        ) : item.external ? (
          <a
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-3 px-3 rounded-lg font-medium transition-all duration-200",
              depth === 0 ? "py-2.5 text-sm" : "py-2 text-xs",
              "text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple"
            )}
          >
            <Icon className={cn(depth === 0 ? "h-5 w-5" : "h-4 w-4", "text-gray-500")} />
            {item.label}
          </a>
        ) : item.modalPdf ? (
          <button
            onClick={() => { openPdfViewer(item.modalPdf!); onNavigate?.() }}
            className={cn(
              "w-full flex items-center gap-3 px-3 rounded-lg font-medium transition-all duration-200",
              depth === 0 ? "py-2.5 text-sm" : "py-2 text-xs",
              "text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple"
            )}
          >
            <Icon className={cn(depth === 0 ? "h-5 w-5" : "h-4 w-4", "text-gray-500")} />
            {item.label}
          </button>
        ) : (
          <NavLink
            to={item.path}
            end
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 rounded-lg font-medium transition-all duration-200",
                depth === 0 ? "py-2.5 text-sm" : "py-2 text-xs",
                isActive
                  ? "bg-cofarte-purple text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-100 hover:text-cofarte-purple"
              )
            }
          >
            <Icon className={cn(
              depth === 0 ? "h-5 w-5" : "h-4 w-4",
              location.pathname === item.path ? "text-white" : "text-gray-500"
            )} />
            {item.label}
          </NavLink>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <nav className={cn(
        "flex-1",
        collapsed ? "p-2 space-y-1 overflow-visible" : "p-4 space-y-1 overflow-y-auto"
      )}>
        {menuItems.map(item => 
          collapsed ? renderCollapsedItem(item) : renderExpandedItem(item)
        )}
      </nav>

      {user && !collapsed && (
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="h-8 w-8 rounded-full bg-cofarte-purple/10 flex items-center justify-center">
              <UserCircle className="h-4 w-4 text-cofarte-purple" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user.name1} {user.name2}</p>
              <p className="text-xs text-gray-500 truncate">{demoMode ? '—' : user.kunnr}</p>
            </div>
          </div>
        </div>
      )}

      {user && collapsed && (
        <div className="p-2 border-t border-gray-100">
          <div className="flex items-center justify-center py-2">
            <div className="h-8 w-8 rounded-full bg-cofarte-purple/10 flex items-center justify-center" title={`${user.name1} ${user.name2}`}>
              <UserCircle className="h-4 w-4 text-cofarte-purple" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
